import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useState } from 'react';
import { ViewModel as CreditSafeLookupViewModel } from '../../TypeGen/Customer/CreditSafe/Lookup/view-model';
import { ViewModel as CreditSafeReportViewModel } from '../../TypeGen/Customer/CreditSafe/Report/view-model';
import LoadingPanel from '../../components/LoadingPanel';
import { fetchApi } from '../../services/api';
import { Title } from '../../utils/title';
import { isNullOrWhiteSpace } from '../../utils/utils';
import { formatNumber } from '@progress/kendo-intl';

const CreditScore = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const [name, setName] = useState(urlParams.get('name') || '');
  const [city, setCity] = useState(urlParams.get('city') || '');
  const [state, setState] = useState(urlParams.get('state') || '');
  const [zip, setZip] = useState(urlParams.get('zip') || '');
  const [country, setCountry] = useState(urlParams.get('country'));
  const [address, setAddress] = useState(urlParams.get('address') || '');
  const [result, setResult] = useState<CreditSafeLookupViewModel[]>([]);

  const [companyReport, setCompanyReport] = useState<CreditSafeReportViewModel>();
  const [loading, setLoading] = useState(false);
  const [companyReportLoading, setCompanyReportLoading] = useState(false);

  const submit = () => {
    setLoading(true);
    fetchApi('/api/Customer/CreditSafeCompanySearch',
      {
        CompanyName: name,
        Address: address,
        City: city,
        State: state,
        Zip: zip,
        Country: country
      }, 'POST')
      .then((data: CreditSafeLookupViewModel[]) => setResult(data))
      .finally(() => setLoading(false));
  };

  const getCompanyReport = (selectedCompany: CreditSafeLookupViewModel) => {
    setCompanyReportLoading(true);
    fetchApi(`/api/Customer/CreditSafeCompanyReport/${selectedCompany.CompanyID}`)
      .then((data: CreditSafeReportViewModel) => setCompanyReport(data))
      .finally(() => setCompanyReportLoading(false));
  }

  const displayText = (s?: string) => (s?.trim() ? s : "-");
  const displayLink = (url?: string) =>
    url && url.trim() ? (
      <a href={/^https?:\/\//i.test(url) ? url : `https://${url}`} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
    ) : (
      "-"
    );

  return (
    <div className="container-fluid mt-3" style={{ width: "90%" }}>
      <Title string="Credit Score" />
      {/*<p>*Results may take 25-60 seconds to generate.</p>*/}
      <div className="container">
        <div className="form-group">
          <label>Customer Name</label>
          <input className="form-control" required value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Address</label>
          <input className="form-control" required value={address} onChange={e => setAddress(e.target.value)} />
        </div>
        <div className="form-group">
          <label>City</label>
          <input className="form-control" required value={city} onChange={e => setCity(e.target.value)} />
        </div>
        <div className="form-group">
          <label>State</label>
          <input className="form-control" required value={state} onChange={e => setState(e.target.value)} maxLength={2} />
        </div>
        <div className="form-group">
          <label>Zip</label>
          <input className="form-control" required value={zip} onChange={e => setZip(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Country</label>
          <DropDownList
            //style={{ width: '300px' }}
            data={["USA", "CAN", "MEX"]}
            value={country}
            onChange={(e: DropDownListChangeEvent) => setCountry(e.target.value)}
          />
        </div>
        <button className="btn btn-primary"
          disabled={loading || isNullOrWhiteSpace(name) || isNullOrWhiteSpace(address) || isNullOrWhiteSpace(city) || isNullOrWhiteSpace(state) || isNullOrWhiteSpace(zip) || isNullOrWhiteSpace(country)}
          onClick={submit}>Check</button>
      </div>
      {loading && <LoadingPanel />}
      {companyReportLoading && <LoadingPanel />}
      {result && !loading && (
        <div className="mt-5">
          {result.length > 0 && (
            <>
              <div className="row fw-bold border-bottom pb-2 mb-2">
                <div className="col">ID</div>
                <div className="col">Company Name</div>
                <div className="col">Address</div>
                <div className="col">City / State / Zip</div>
                <div className="col">Office Type</div>
                <div className="col">Reg No</div>
                <div className="col">Safe No</div>
                <div className="col">VAT No</div>
              </div>

              {result.map((company, idx) => (
                <div className="row py-3" key={idx} style={{ cursor: "pointer" }} onClick={() => getCompanyReport(company)}>
                  <div className="col">{company.CompanyID}</div>
                  <div className="col">{company.CompanyName}</div>
                  <div className="col">{company.Address}</div>
                  <div className="col">
                    {company.City}, {company.State} {company.Zip}
                  </div>
                  <div className="col">{company.OfficeType}</div>
                  <div className="col">{company.RegNo}</div>
                  <div className="col">{company.SafeNo}</div>
                  <div className="col">{company.VatNo}</div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {companyReport && <Dialog title={''} onClose={() => setCompanyReport(null)}>
        <div className="container-fluid">
          {/* Header / Names */}
          <div className="row g-2 mb-3">
            <div className="col-12">
              <h5 className="mb-1">{displayText(companyReport.BusinessName)}</h5>
              <div className="text-muted small">
                Registered Name: {displayText(companyReport.RegisteredCompanyName)}
              </div>
            </div>
          </div>

          {/* Core Info */}
          <div className="row g-3">
            <div className="col-md-6">
              <div className="font-weight-bold">Location</div>
              <div>{displayText(companyReport.CityStateZip)}</div>
            </div>

            <div className="col-md-6">
              <div className="font-weight-bold">Industry Sector</div>
              <div>{displayText(companyReport.IndustrySector)}</div>
            </div>

            <div className="col-md-6">
              <div className="font-weight-bold">Telephone</div>
              <div>
                {companyReport.Telephone?.trim() ? (
                  <a href={`tel:${companyReport.Telephone}`}>{displayLink(companyReport.Telephone)}</a>
                ) : (
                  "—"
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="font-weight-bold">Website</div>
              <div>{displayLink(companyReport.Website)}</div>
            </div>
          </div>

          <hr className="my-3" />

          {/* Credit Details */}
          <div className="row g-3">
            <div className="col-md-4">
              <div className="font-weight-bold">Credit Rating Score</div>
              <div>{displayText(companyReport.CreditRatingScore)} [{displayText(companyReport.CreditRatingScoreNumericValue)}]</div>
            </div>

            <div className="col-md-4">
              <div className="font-weight-bold">Credit Description</div>
              <div>{displayText(companyReport.CreditRatingScoreDescription)}</div>
            </div>

            <div className="col-md-4">
              <div className="font-weight-bold">Credit Limit</div>
              <div>{formatNumber(Number(companyReport.CreditRatingCreditLimit), "N0")}</div>
            </div>
          </div>
        </div>
        <DialogActionsBar>
          <Button type="button" onClick={() => setCompanyReport(null)}>
            Close
          </Button>
        </DialogActionsBar>
      </Dialog>}
    </div>
  );
};

export default CreditScore;